/**
 * Cấu hình dùng chung cho trang "Lịch làm việc Teamlead" (team-leads-calendar).
 *
 * - HR (phòng Nhân sự - department_id = 3) và Nhân sự tổ chức giáo viên
 *   (department_id = 52) là người thao tác đặt lịch họp cho Teamlead và theo dõi lịch rảnh.
 * - Teamlead là tài khoản có account_type_id = 16 (TEAMLEADER). Teamlead dùng trang này
 *   như một công cụ làm việc: theo dõi lịch, ghi chú, nhắc lịch, thông báo task.
 */
import moment from "moment-timezone";
import { TEAMLEADER } from "@/core/config/accountTypeOption";

/** account_type_id của Teamlead */
export const TEAM_LEAD_ACCOUNT_TYPE_ID = TEAMLEADER; // = 16

/** Phòng Nhân sự */
export const DEPARTMENT_HR = 3;
/** Nhân sự tổ chức giáo viên (team TCGV) */
export const DEPARTMENT_TCGV = 52;
/** Các phòng ban được quyền thao tác như HR trên trang này */
export const HR_DEPARTMENT_IDS = [DEPARTMENT_HR, DEPARTMENT_TCGV];

/** Vai trò người dùng khi vào trang */
export const ROLE = {
    HR: "hr",
    TEAM_LEAD: "team_lead",
    VIEWER: "viewer",
};

/** Nhãn cho lịch chưa gắn giờ */
export const ALL_DAY_LABEL = "Cả ngày";

/** Lịch không có giờ kết thúc thì coi như kéo dài bấy nhiêu phút */
export const DEFAULT_EVENT_MINUTES = 60;

/** "08:00" | "08:00:00" -> 480; rỗng/không hợp lệ -> null */
export function hmToMinutes(value) {
    const m = value ? String(value).match(/^(\d{1,2}):(\d{2})/) : null;
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** 480 -> "08:00" */
export function minutesToHm(total) {
    const v = Math.max(0, Math.min(24 * 60, Number(total) || 0));
    return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
}

/**
 * Khoảng thời gian của 1 sự kiện, tính bằng phút kể từ 00:00.
 * Trả về null khi sự kiện không có giờ — những lịch này hiển thị là "Cả ngày".
 */
export function eventTimeRange(ev) {
    const start = hmToMinutes(ev && ev.start_time);
    if (start == null) return null;
    let end = hmToMinutes(ev && ev.end_time);
    if (end == null || end <= start) end = start + DEFAULT_EVENT_MINUTES;
    return { start, end };
}

/** 2 lịch có đè giờ lên nhau không (lịch chưa gắn giờ coi như chiếm cả ngày) */
export function eventsOverlap(a, b) {
    const ra = eventTimeRange(a);
    const rb = eventTimeRange(b);
    if (!ra || !rb) return true;
    return ra.start < rb.end && ra.end > rb.start;
}

/** Nhãn giờ của 1 sự kiện: "08:00–10:00", "08:00" hoặc "Cả ngày" */
export function eventTimeLabel(ev) {
    if (!ev || !ev.start_time) return ALL_DAY_LABEL;
    return ev.end_time ? `${ev.start_time}–${ev.end_time}` : ev.start_time;
}

/** Loại sự kiện trên lịch */
export const EVENT_TYPE = {
    /** HR đặt lịch họp cho Teamlead */
    MEETING: "meeting",
    /** Lịch cố định họp phòng (cho cả team TCGV) */
    DEPARTMENT_MEETING: "department_meeting",
    /** Ghi chú cá nhân của Teamlead / HR ghi chú giúp */
    NOTE: "note",
    /** Teamlead báo bận (không nhận lịch họp) */
    BUSY: "busy",
    /** Task công việc cần nhắc */
    TASK: "task",
};

export const EVENT_TYPE_META = {
    [EVENT_TYPE.DEPARTMENT_MEETING]: {
        label: "Họp phòng",
        color: "#4f46e5",
        bg: "#eef2ff",
        icon: "el-icon-office-building",
        tag: "",
    },
    [EVENT_TYPE.MEETING]: {
        label: "Lịch họp",
        color: "#0ea5e9",
        bg: "#e0f2fe",
        icon: "el-icon-chat-line-square",
        tag: "info",
    },
    [EVENT_TYPE.NOTE]: {
        label: "Ghi chú",
        color: "#d97706",
        bg: "#fef3c7",
        icon: "el-icon-edit-outline",
        tag: "warning",
    },
    [EVENT_TYPE.BUSY]: {
        label: "Bận",
        color: "#64748b",
        bg: "#f1f5f9",
        icon: "el-icon-remove-outline",
        tag: "info",
    },
    [EVENT_TYPE.TASK]: {
        label: "Task",
        color: "#059669",
        bg: "#d1fae5",
        icon: "el-icon-s-claim",
        tag: "success",
    },
};

export const EVENT_TYPE_OPTIONS_HR = [
    { value: EVENT_TYPE.MEETING, label: "Lịch họp" },
    { value: EVENT_TYPE.DEPARTMENT_MEETING, label: "Họp phòng (cố định)" },
    { value: EVENT_TYPE.NOTE, label: "Ghi chú" },
];

export const EVENT_TYPE_OPTIONS_TEAM_LEAD = [
    { value: EVENT_TYPE.NOTE, label: "Ghi chú" },
    { value: EVENT_TYPE.BUSY, label: "Báo bận" },
    { value: EVENT_TYPE.TASK, label: "Task / nhắc việc" },
];

/** Trạng thái của một sự kiện */
export const EVENT_STATUS = {
    SCHEDULED: "scheduled",
    DONE: "done",
    CANCELLED: "cancelled",
};

export const EVENT_STATUS_META = {
    [EVENT_STATUS.SCHEDULED]: { label: "Đã lên lịch", type: "" },
    [EVENT_STATUS.DONE]: { label: "Hoàn thành", type: "success" },
    [EVENT_STATUS.CANCELLED]: { label: "Đã huỷ", type: "danger" },
};

/** Phản hồi của Teamlead với lịch họp do HR đặt */
export const MEETING_RESPONSE = {
    PENDING: "pending",
    ACCEPTED: "accepted",
    DECLINED: "declined",
};

export const MEETING_RESPONSE_META = {
    [MEETING_RESPONSE.PENDING]: { label: "Chờ xác nhận", type: "warning" },
    [MEETING_RESPONSE.ACCEPTED]: { label: "Đã nhận lịch", type: "success" },
    [MEETING_RESPONSE.DECLINED]: { label: "Từ chối", type: "danger" },
};

/** Kiểu lặp khi tạo ghi chú / lịch */
export const RECURRENCE_MODE = {
    /** Chỉ áp dụng cho ngày đang chọn */
    ONCE: "once",
    /** Lặp lại hàng tuần - áp dụng 1 lần cho toàn bộ các tuần */
    WEEKLY: "weekly",
};

/** Phạm vi của quy tắc lặp */
export const RECURRENCE_SCOPE = {
    /** Cả team (vd toàn bộ team TCGV) */
    TEAM: "team",
    /** Một Teamlead cụ thể */
    INDIVIDUAL: "individual",
};

export const RECURRENCE_SCOPE_META = {
    [RECURRENCE_SCOPE.TEAM]: { label: "Toàn team", type: "" },
    [RECURRENCE_SCOPE.INDIVIDUAL]: { label: "Cá nhân", type: "info" },
};

/**
 * Thứ trong tuần theo chuẩn ISO: 1 = Thứ 2 ... 7 = Chủ nhật.
 * `mIndex` là getDay() của Javascript (0 = CN).
 */
export const WEEKDAYS = [
    { value: 1, label: "Thứ 2", short: "T2", mIndex: 1 },
    { value: 2, label: "Thứ 3", short: "T3", mIndex: 2 },
    { value: 3, label: "Thứ 4", short: "T4", mIndex: 3 },
    { value: 4, label: "Thứ 5", short: "T5", mIndex: 4 },
    { value: 5, label: "Thứ 6", short: "T6", mIndex: 5 },
    { value: 6, label: "Thứ 7", short: "T7", mIndex: 6 },
    { value: 7, label: "Chủ nhật", short: "CN", mIndex: 0 },
];

export const WEEKDAY_LABEL = WEEKDAYS.reduce((acc, d) => {
    acc[d.value] = d.label;
    return acc;
}, {});

export const WEEKDAY_SHORT = WEEKDAYS.reduce((acc, d) => {
    acc[d.value] = d.short;
    return acc;
}, {});

/** Đổi Date -> thứ ISO (1..7) */
export function isoWeekday(date) {
    const d = date instanceof Date ? date.getDay() : new Date(date).getDay();
    return d === 0 ? 7 : d;
}

/** Tuỳ chọn nhắc trước giờ diễn ra (phút) */
export const REMIND_BEFORE_OPTIONS = [
    { value: 0, label: "Không nhắc" },
    { value: 15, label: "Trước 15 phút" },
    { value: 30, label: "Trước 30 phút" },
    { value: 60, label: "Trước 1 giờ" },
    { value: 120, label: "Trước 2 giờ" },
    { value: 1440, label: "Trước 1 ngày" },
];

/** Số ngày tới được coi là "lịch sắp diễn ra" để nhắc trên panel */
export const UPCOMING_WINDOW_DAYS = 3;

/* ------------------------------------------------------------------ *
 * Phạm vi áp dụng của lịch cố định
 * ------------------------------------------------------------------ *
 * Lịch cố định được khai báo 1 lần theo các thứ trong tuần. Ngoài ngày bắt đầu,
 * người tạo chọn thêm phạm vi áp dụng: một số tuần/tháng nhất định, hoặc không
 * giới hạn (lặp mãi). Phạm vi được quy đổi thành `end_date` gửi cho backend.
 */
export const FIXED_SCHEDULE_UNLIMITED = "unlimited";

export const FIXED_SCHEDULE_DURATIONS = [
    { value: FIXED_SCHEDULE_UNLIMITED, label: "Không giới hạn (lặp mãi)", amount: 0, unit: null },
    { value: "2w", label: "2 tuần", amount: 2, unit: "weeks" },
    { value: "4w", label: "4 tuần", amount: 4, unit: "weeks" },
    { value: "8w", label: "8 tuần", amount: 8, unit: "weeks" },
    { value: "1m", label: "1 tháng", amount: 1, unit: "months" },
    { value: "3m", label: "3 tháng", amount: 3, unit: "months" },
    { value: "6m", label: "6 tháng", amount: 6, unit: "months" },
    { value: "12m", label: "1 năm", amount: 12, unit: "months" },
    { value: "custom", label: "Chọn ngày kết thúc...", amount: 0, unit: null },
];

export function findFixedScheduleDuration(value) {
    return FIXED_SCHEDULE_DURATIONS.find((d) => d.value === value) || null;
}

/** "YYYY-MM-DD" của ngày kết thúc phạm vi; null = không giới hạn */
export function fixedScheduleEndDate(startDate, durationValue) {
    const preset = findFixedScheduleDuration(durationValue);
    if (!startDate || !preset || !preset.unit) return null;
    // Trừ 1 ngày để "4 tuần kể từ 07/09" kết thúc đúng ngày 04/10 (đủ 4 tuần tròn)
    return moment(startDate, "YYYY-MM-DD").add(preset.amount, preset.unit).subtract(1, "days").format("YYYY-MM-DD");
}

/** Lịch cố định đã hết hạn áp dụng (ngày kết thúc nằm trước hôm nay) */
export function isFixedScheduleExpired(rule) {
    if (!rule || !rule.effective_to) return false;
    return rule.effective_to < moment().format("YYYY-MM-DD");
}

/** "Áp dụng từ 07/09/2026 đến 04/10/2026" / "... (không giới hạn)" */
export function fixedScheduleRangeText(rule) {
    if (!rule || !rule.effective_from) return "Áp dụng từ: chưa đặt";
    const from = moment(rule.effective_from).format("DD/MM/YYYY");
    if (!rule.effective_to) return `Áp dụng từ ${from} · không giới hạn`;
    return `Áp dụng ${from} – ${moment(rule.effective_to).format("DD/MM/YYYY")}`;
}

/**
 * Bảng màu phong phú, hiện đại dùng cho các task / lịch trên calendar
 */
export const TASK_COLOR_PALETTES = [
    {
        name: "blue",
        label: "Xanh dương",
        bg: "#eff6ff",
        border: "#bfdbfe",
        text: "#1d4ed8",
        dot: "#3b82f6",
        time: "#1e40af",
    },
    {
        name: "emerald",
        label: "Xanh lục",
        bg: "#ecfdf5",
        border: "#a7f3d0",
        text: "#047857",
        dot: "#10b981",
        time: "#065f46",
    },
    {
        name: "indigo",
        label: "Tím than",
        bg: "#eef2ff",
        border: "#c7d2fe",
        text: "#4338ca",
        dot: "#6366f1",
        time: "#3730a3",
    },
    {
        name: "purple",
        label: "Tím hoa cà",
        bg: "#f5f3ff",
        border: "#ddd6fe",
        text: "#6d28d9",
        dot: "#8b5cf6",
        time: "#5b21b6",
    },
    {
        name: "amber",
        label: "Cam hổ phách",
        bg: "#fffbeb",
        border: "#fde68a",
        text: "#b45309",
        dot: "#f59e0b",
        time: "#92400e",
    },
    {
        name: "cyan",
        label: "Xanh ngọc",
        bg: "#ecfeff",
        border: "#a5f3fc",
        text: "#0e7490",
        dot: "#06b6d4",
        time: "#155e75",
    },
    {
        name: "rose",
        label: "Hồng đào",
        bg: "#fff1f2",
        border: "#fecdd3",
        text: "#be123c",
        dot: "#f43f5e",
        time: "#9f1239",
    },
    {
        name: "teal",
        label: "Xanh mòng két",
        bg: "#f0fdfa",
        border: "#99f6e4",
        text: "#0f766e",
        dot: "#14b8a6",
        time: "#115e59",
    },
    {
        name: "orange",
        label: "Cam tươi",
        bg: "#fff7ed",
        border: "#fed7aa",
        text: "#c2410c",
        dot: "#f97316",
        time: "#9a3412",
    },
    {
        name: "violet",
        label: "Tím violet",
        bg: "#faf5ff",
        border: "#e9d5ff",
        text: "#7e22ce",
        dot: "#a855f7",
        time: "#6b21a8",
    },
];

/**
 * Trả về màu tương ứng cho 1 task theo quy tắc:
 * 1. Màu chỉ định rõ trên task.color / raw.color
 * 2. Phỏng vấn (recruitment_interview_id) -> Tím (purple)
 * 3. Họp / meeting -> Xanh dương (blue/indigo)
 * 4. Báo bận -> Hồng đào (rose)
 * 5. Hash nội dung tên task / id để gán màu nhất quán và đa dạng
 */
export function getTaskThemeColor(task) {
    if (!task) return TASK_COLOR_PALETTES[0];

    const explicit = task.color || (task.raw && task.raw.color);
    if (explicit) {
        const found = TASK_COLOR_PALETTES.find((c) => c.name === explicit);
        if (found) return found;
    }

    if (task.recruitment_interview_id || task.type === "interview" || (task.raw && task.raw.recruitment_interview_id)) {
        return TASK_COLOR_PALETTES[3]; // purple
    }

    if (task.type === EVENT_TYPE.DEPARTMENT_MEETING || task.type === EVENT_TYPE.MEETING) {
        return TASK_COLOR_PALETTES[0]; // blue
    }

    if (task.type === EVENT_TYPE.BUSY) {
        return TASK_COLOR_PALETTES[6]; // rose
    }

    const seed = String(task.task || task.title || task.id || "");
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    const idx = Math.abs(hash) % TASK_COLOR_PALETTES.length;
    return TASK_COLOR_PALETTES[idx];
}

